import React, { useEffect, useState } from "react";
import "../component/style.css";
import SideNav from "../component/sideNav";
import Header from "../component/header";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { PermissionApi } from "../apis";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InsightsIcon from "@mui/icons-material/Insights";
import EditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CustomInput from "../component/customInput";

const Permission = () => {
  const [permissions, setPermissions] = useState([]);
  const [paginator, setPaginator] = useState({
    page: 0,
    perPage: 10,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [displayAddModal, setDisplayAddModal] = useState(false);
  const [displayEditModal, setDisplayEditModal] = useState(false);
  const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState({});
  const permissionRouter = PermissionApi();

  useEffect(() => {
    console.log("... total count effect ", totalCount);
  }, [totalCount]);

  // Attributes
  const [name, setName] = useState("");
  const [enums, setEnums] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  //err attributes
  const [nameErr, setNameErr] = useState("");
  const [enumErr, setEnumErr] = useState("");

  const columns = [
    {
      field: "name",
      headerName: (
        <div
          style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
        >
          Name
        </div>
      ),
      flex: 1,
      renderCell: (params) => params?.value,
      sortable: false,
    },
    {
      field: "enums",
      headerName: (
        <div
          style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
        >
          Enum
        </div>
      ),
      flex: 1,
      renderCell: (params) => params?.enums,
      sortable: false,
    },
    {
      field: "updatedAt",
      headerName: (
        <div
          style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
        >
          Updated At
        </div>
      ),
      flex: 1,
      renderCell: ({ row }) => {
        const dateString = row?.updatedAt;
        if (!dateString) return "";
        const dateObject = new Date(dateString);
        const formattedDate = dateObject.toISOString().split("T")[0];

        return formattedDate;
      },
      sortable: false,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 150,
      getActions: ({ row }) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="Detail" placement="top">
              <InsightsIcon style={{ color: "green", width: 24, height: 24 }} />
            </Tooltip>
          }
          label="Detail"
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Edit" placement="top">
              <EditIcon style={{ color: "#27326A", width: 24, height: 24 }} />
            </Tooltip>
          }
          label="Edit"
          onClick={() => {
            setSelectedRecord(row);
            setName(row?.name);
            toggleEditModal();
          }}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Delete" placement="top">
              <DeleteIcon style={{ color: "red", width: 24, height: 24 }} />
            </Tooltip>
          }
          label="Delete"
          onClick={() => {
            setSelectedRecord(row);
            toggleDeleteModal();
          }}
        />,
      ],
    },
  ];

  useEffect(() => {
    getAllPermissions();
    // eslint-disable-next-line
  }, [paginator]);

  const toggleAddModal = () => setDisplayAddModal(!displayAddModal);
  const toggleEditModal = () => setDisplayEditModal(!displayEditModal);
  const toggleDeleteModal = () => setDisplayDeleteModal(!displayDeleteModal);

  const getAllPermissions = async ({ search = "" } = {}) => {
    try {
      const res = await permissionRouter.getAllPaginated({
        page: paginator.page,
        pageSize: paginator.perPage,
        search: search,
      });
      if (res?.data) {
        setPermissions(res?.data);
        setTotalCount(res?.pagination?.totalCount);
      }
    } catch (error) {
      console.log("Permissions ", error);
    }
  };

  const createPermission = async () => {
    try {
      const isValid = validate();
      if (!isValid) return null;
      const res = await permissionRouter.create({
        name,
        permissionEnum: enums,
      });
      if (res.status !== 200 && res.status !== 201) {
        const msg =
          res?.error?.response?.data?.message || "An unknown error occurred";
        setEnumErr(msg);
      } else {
        setEnumErr("");
        toggleAddModal();
        setName("");
        setEnums("");
        getAllPermissions();
      }
    } catch (error) {
      console.log("Permissions ", error);
    }
  };

  const updatePermission = async () => {
    try {
      const isValid = validate(true);
      if (!isValid) return null;
      const res = await permissionRouter.update({ name }, selectedRecord?._id);
      if (res) {
        console.log(res);
        toggleEditModal();
        setName("");
        setEnums("");
        getAllPermissions();
      }
    } catch (error) {
      console.log("Permissions ", error);
    }
  };

  const removePermission = async () => {
    try {
      const id = selectedRecord?._id;
      const res = await permissionRouter.remove(id);
      if (res) {
        toggleDeleteModal();
        getAllPermissions();
      }
    } catch (error) {
      console.log("Permissions ", error);
    }
  };

  const validate = (isUpdate = false) => {
    let isValid = true;
    if (!name) {
      setNameErr("name is required");
      isValid = false;
    }

    if (!enums && !isUpdate) {
      setEnumErr("enum is required");
      isValid = false;
    }

    return isValid;
  };

  const debounce = (func, delay) => {
    let timeOut;
    return (...args) => {
      clearTimeout(timeOut);
      timeOut = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debounceSearch = debounce(getAllPermissions, 500);

  const handleSearchInput = (e) => {
    const search = e.target.value;
    setSearchTerm(search);
    debounceSearch({ search });
  };

  return (
    <>
      <div className="mainContainer">
        {/* Add Dialog */}
        <Dialog open={displayAddModal} onClose={toggleAddModal}>
          <DialogTitle>Create Permission</DialogTitle>
          <DialogContent>
            <CustomInput
              id="name"
              name="name"
              title="Enter name"
              titleRequired={true}
              isRequired={true}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameErr("");
              }}
              type="text"
              placeholder="enter name"
              required={nameErr.length > 0 ? true : false}
              helperText={nameErr}
              width="100%"
            />

            <CustomInput
              id="enum"
              name="enum"
              title="Enter enum"
              titleRequired={true}
              isRequired={true}
              value={enums}
              onChange={(e) => {
                setEnums(e.target.value);
                setEnumErr("");
              }}
              type="text"
              placeholder="enter enum"
              required={enumErr.length > 0 ? true : false}
              helperText={enumErr}
              width="100%"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={toggleAddModal}>Cancel</Button>
            <Button onClick={() => createPermission()}>Create</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog  */}
        <Dialog open={displayEditModal} onClose={toggleEditModal}>
          <DialogTitle>Edit Permission</DialogTitle>
          <DialogContent>
            <CustomInput
              id="name"
              name="name"
              title="Enter name"
              titleRequired={true}
              isRequired={true}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameErr("");
              }}
              type="text"
              placeholder="enter name"
              required={nameErr.length > 0 ? true : false}
              helperText={nameErr}
              width="100%"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={toggleEditModal}>Cancel</Button>
            <Button onClick={() => updatePermission()}>Update</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog  */}
        <Dialog open={displayDeleteModal} onClose={toggleDeleteModal}>
          <DialogTitle>Remove Permission</DialogTitle>
          <DialogContent>
            <Box>
              <p
                style={{
                  margin: "30px 0px -30px",
                }}
              >
                Are you sure you want to delete this permission?
              </p>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button color="error" onClick={toggleDeleteModal}>
              Cancel
            </Button>
            <Button onClick={() => removePermission()}>
              Remove Permission
            </Button>
          </DialogActions>
        </Dialog>

        <Header></Header>
        <SideNav active="Permissions" />
        <div className="contentBox">
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "1%",
              marginTop: "1%",
              gap: "20px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              margin="dense"
              variant="outlined"
              id="outlined-start-adornment"
              sx={{ m: 1, width: "25ch" }}
              onChange={handleSearchInput}
              value={searchTerm}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon></SearchIcon>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant="contained"
              size="small"
              style={{
                backgroundColor: "#27326A",
                width: "148px",
                height: "40px",
                textTransform: "none",
              }}
              onClick={() => {
                toggleAddModal();
                setName();
                setEnums();
                setEnumErr("");
                setNameErr("");
              }}
            >
              <AddCircleOutlineIcon
                sx={{ marginRight: "10px", width: 18, height: 18 }}
              />
              Add Permission
            </Button>
          </Box>
          <DataGrid
            sx={{
              borderRadius: 3,
              border: 0,
              width: "100%",
              outline: "none", // Remove blue outline
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold !important",
                overflow: "visible !important",
                color: "#27326A",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-sortIcon": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                {
                  outline: "none", // Remove blue outline
                },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none", // Remove blue outline
              },
              "& .MuiDataGrid-cell": {
                outline: "none", // Remove blue outline from cells
              },
              "& .MuiDataGrid-cell--textLeft": {
                textAlign: "left",
              },
              "& .MuiDataGrid-cell--textCenter": {
                textAlign: "center",
              },
              "& .MuiDataGrid-cell--textRight": {
                textAlign: "right",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "transparent !important",
              },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "transparent !important",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "white",
                boxShadow: "0px 0px 30px #EDEEF4",
                outline: "none",
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                outline: "none",
              },
              "& .name-cell-style": {
                color: "#566477",
                outline: "none",
              },
              "& .default-cell-style": {
                color: "#95A2B4",
                outline: "none",
              },
              "& .MuiDataGrid-row": {
                borderBottom: "none",
                outline: "none",
              },
              "& .MuiDataGrid-columnHeaders": {},
            }}
            disableColumnMenu
            disableSelectionOnClick
            getRowId={(row) => row._id}
            columns={columns}
            rows={permissions}
            paginationMode="server"
            initialState={{
              filter: {
                filterModel: {
                  items: [],
                  quickFilterExcludeHiddenColumns: true,
                },
              },
              pagination: {
                paginationModel: { page: 0, pageSize: paginator.perPage },
              },
            }}
            rowCount={totalCount}
            onPaginationModelChange={(newPage) => {
              setPaginator({
                page: newPage?.page,
                perPage: newPage?.pageSize,
              });
            }}
            pageSizeOptions={[10]}
          ></DataGrid>
        </div>
      </div>
    </>
  );
};

export default Permission;
